import React, { useState, useEffect } from 'react';
import {
  Settings, AlertTriangle, Shield, Eye, Users,
  Scale, BarChart2, Save
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '../../api/core.js';

function PerformanceScoringConfig({ onMessage }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [config, setConfig] = useState({
    is_enabled: false,
    scoring_purpose: 'safety_improvement',
    requires_human_review: true,
    review_threshold_score: 50,
    show_score_to_driver: true,
    show_breakdown_to_driver: true,
    show_improvement_tips: true,
    appeal_enabled: true,
    appeal_contact: 'rh@entreprise.fr',
    appeal_deadline_days: 30,
    no_automatic_discipline: true,
    no_bonus_penalty: false,
    uses_location_data: true,
    uses_speed_data: true,
    uses_harsh_events: true,
    uses_idle_time: false,
    uses_camera_data: false,
    weight_speeding: 30,
    weight_harsh_braking: 25,
    weight_rapid_acceleration: 20,
    weight_idle_time: 15,
    weight_fuel_efficiency: 10
  });

  // Fetch performance scoring config (tenant-isolated via middleware)
  const { data: configData } = useQuery({
    queryKey: ['performance_scoring_config'],
    queryFn: () => apiRequest('/gdpr-compliance/performance-scoring')
  });

  useEffect(() => {
    if (configData?.data && Object.keys(configData.data).length > 0) {
      setConfig(prev => ({ ...prev, ...configData.data }));
    }
  }, [configData]);

  // Save performance scoring config (org_id injected from JWT by middleware)
  const saveMutation = useMutation({
    mutationFn: (data) => apiRequest('/gdpr-compliance/performance-scoring', {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(['performance_scoring_config']);
      onMessage?.({ type: 'success', text: t('compliance.scoring.toast.saved') });
    }
  });

  const handleSave = () => {
    saveMutation.mutate(config);
  };

  const totalWeight = config.weight_speeding + config.weight_harsh_braking +
    config.weight_rapid_acceleration + config.weight_idle_time + config.weight_fuel_efficiency;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center">
            <BarChart2 className="w-5 h-5 mr-2 text-blue-600" />
            {t('compliance.scoring.title')}
          </h3>
          <p className="text-sm text-gray-500">
            {t('compliance.scoring.subtitle')}
          </p>
        </div>
        <button
          onClick={handleSave}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
        >
          <Save className="w-4 h-4 mr-2" />
          {t('compliance.scoring.save')}
        </button>
      </div>

      {/* GDPR Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertTriangle className="w-5 h-5 text-amber-600 mr-3 mt-0.5" />
          <div>
            <h4 className="font-semibold text-amber-800">{t('compliance.scoring.warning.title')}</h4>
            <p className="text-sm text-amber-700 mt-1">
              {t('compliance.scoring.warning.body')}
            </p>
            <ul className="text-sm text-amber-700 mt-2 space-y-1 list-disc list-inside">
              <li>{t('compliance.scoring.warning.items.consent')}</li>
              <li>{t('compliance.scoring.warning.items.humanReview')}</li>
              <li>{t('compliance.scoring.warning.items.appeal')}</li>
              <li>{t('compliance.scoring.warning.items.explain')}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Enable/Disable */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-semibold">{t('compliance.scoring.enable.title')}</h4>
            <p className="text-sm text-gray-500">{t('compliance.scoring.enable.hint')}</p>
          </div>
          <Toggle
            checked={config.is_enabled}
            onChange={v => setConfig({ ...config, is_enabled: v })}
          />
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('compliance.scoring.purpose.label')}</label>
          <select
            value={config.scoring_purpose}
            onChange={e => setConfig({ ...config, scoring_purpose: e.target.value })}
            className="w-full md:w-1/2 border rounded-lg px-3 py-2"
          >
            <option value="safety_improvement">{t('compliance.scoring.purpose.options.safety_improvement')}</option>
            <option value="fuel_efficiency">{t('compliance.scoring.purpose.options.fuel_efficiency')}</option>
            <option value="training">{t('compliance.scoring.purpose.options.training')}</option>
            <option value="fleet_optimization">{t('compliance.scoring.purpose.options.fleet_optimization')}</option>
          </select>
        </div>
      </div>

      {/* Human Review Requirements */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="font-semibold mb-4 flex items-center">
          <Users className="w-5 h-5 mr-2 text-purple-600" />
          {t('compliance.scoring.humanReview.title')}
        </h4>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <span className="font-medium">{t('compliance.scoring.humanReview.required')}</span>
              <p className="text-sm text-gray-500">{t('compliance.scoring.humanReview.requiredHint')}</p>
            </div>
            <Toggle
              checked={config.requires_human_review}
              onChange={v => setConfig({ ...config, requires_human_review: v })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('compliance.scoring.humanReview.threshold')}
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="range"
                min="0"
                max="100"
                value={config.review_threshold_score}
                onChange={e => setConfig({ ...config, review_threshold_score: parseInt(e.target.value) })}
                className="w-48"
              />
              <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                {config.review_threshold_score}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {t('compliance.scoring.humanReview.thresholdHint')}
            </p>
          </div>
        </div>
      </div>

      {/* Transparency */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="font-semibold mb-4 flex items-center">
          <Eye className="w-5 h-5 mr-2 text-green-600" />
          {t('compliance.scoring.transparency.title')}
        </h4>
        <div className="space-y-4">
          <CheckOption
            t={t}
            label={t('compliance.scoring.transparency.showScore.label')}
            description={t('compliance.scoring.transparency.showScore.description')}
            checked={config.show_score_to_driver}
            onChange={v => setConfig({ ...config, show_score_to_driver: v })}
          />
          <CheckOption
            t={t}
            label={t('compliance.scoring.transparency.showBreakdown.label')}
            description={t('compliance.scoring.transparency.showBreakdown.description')}
            checked={config.show_breakdown_to_driver}
            onChange={v => setConfig({ ...config, show_breakdown_to_driver: v })}
          />
          <CheckOption
            t={t}
            label={t('compliance.scoring.transparency.showTips.label')}
            description={t('compliance.scoring.transparency.showTips.description')}
            checked={config.show_improvement_tips}
            onChange={v => setConfig({ ...config, show_improvement_tips: v })}
          />
        </div>
      </div>

      {/* Appeal Process */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="font-semibold mb-4 flex items-center">
          <Scale className="w-5 h-5 mr-2 text-blue-600" />
          {t('compliance.scoring.appeal.title')}
        </h4>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <span className="font-medium">{t('compliance.scoring.appeal.enabled')}</span>
              <p className="text-sm text-gray-500">{t('compliance.scoring.appeal.enabledHint')}</p>
            </div>
            <Toggle
              checked={config.appeal_enabled}
              onChange={v => setConfig({ ...config, appeal_enabled: v })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('compliance.scoring.appeal.contact')}</label>
            <input
              type="email"
              value={config.appeal_contact}
              onChange={e => setConfig({ ...config, appeal_contact: e.target.value })}
              className="w-full md:w-1/2 border rounded-lg px-3 py-2"
              placeholder="rh@entreprise.fr"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('compliance.scoring.appeal.deadline')}</label>
            <input
              type="number"
              value={config.appeal_deadline_days}
              onChange={e => setConfig({ ...config, appeal_deadline_days: parseInt(e.target.value) })}
              className="w-24 border rounded-lg px-3 py-2"
            />
          </div>
        </div>
      </div>

      {/* Prohibited Uses */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="font-semibold mb-4 flex items-center">
          <Shield className="w-5 h-5 mr-2 text-red-600" />
          {t('compliance.scoring.prohibited.title')}
        </h4>
        <div className="space-y-4">
          <CheckOption
            t={t}
            label={t('compliance.scoring.prohibited.noAutoDiscipline.label')}
            description={t('compliance.scoring.prohibited.noAutoDiscipline.description')}
            checked={config.no_automatic_discipline}
            onChange={v => setConfig({ ...config, no_automatic_discipline: v })}
            recommended
          />
          <CheckOption
            t={t}
            label={t('compliance.scoring.prohibited.noBonusPenalty.label')}
            description={t('compliance.scoring.prohibited.noBonusPenalty.description')}
            checked={config.no_bonus_penalty}
            onChange={v => setConfig({ ...config, no_bonus_penalty: v })}
          />
        </div>
      </div>

      {/* Data Sources */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="font-semibold mb-4 flex items-center">
          <Settings className="w-5 h-5 mr-2 text-gray-600" />
          {t('compliance.scoring.dataSources.title')}
        </h4>
        <p className="text-sm text-gray-500 mb-4">
          {t('compliance.scoring.dataSources.hint')}
        </p>
        <div className="grid grid-cols-2 gap-4">
          <CheckOption
            t={t}
            label={t('compliance.scoring.dataSources.gpsPosition')}
            checked={config.uses_location_data}
            onChange={v => setConfig({ ...config, uses_location_data: v })}
          />
          <CheckOption
            t={t}
            label={t('compliance.scoring.dataSources.speed')}
            checked={config.uses_speed_data}
            onChange={v => setConfig({ ...config, uses_speed_data: v })}
          />
          <CheckOption
            t={t}
            label={t('compliance.scoring.dataSources.harshEvents')}
            checked={config.uses_harsh_events}
            onChange={v => setConfig({ ...config, uses_harsh_events: v })}
          />
          <CheckOption
            t={t}
            label={t('compliance.scoring.dataSources.idleTime')}
            checked={config.uses_idle_time}
            onChange={v => setConfig({ ...config, uses_idle_time: v })}
          />
          <CheckOption
            t={t}
            label={t('compliance.scoring.dataSources.camera')}
            checked={config.uses_camera_data}
            onChange={v => setConfig({ ...config, uses_camera_data: v })}
            warning={t('compliance.scoring.dataSources.cameraWarning')}
          />
        </div>
      </div>

      {/* Weighting */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="font-semibold mb-4 flex items-center">
          <BarChart2 className="w-5 h-5 mr-2 text-blue-600" />
          {t('compliance.scoring.weights.title')}
        </h4>
        <p className="text-sm text-gray-500 mb-4">
          {t('compliance.scoring.weights.hint')}
        </p>
        <div className="space-y-4">
          <WeightSlider
            label={t('compliance.scoring.weights.speeding')}
            value={config.weight_speeding}
            onChange={v => setConfig({ ...config, weight_speeding: v })}
          />
          <WeightSlider
            label={t('compliance.scoring.weights.harshBraking')}
            value={config.weight_harsh_braking}
            onChange={v => setConfig({ ...config, weight_harsh_braking: v })}
          />
          <WeightSlider
            label={t('compliance.scoring.weights.rapidAcceleration')}
            value={config.weight_rapid_acceleration}
            onChange={v => setConfig({ ...config, weight_rapid_acceleration: v })}
          />
          <WeightSlider
            label={t('compliance.scoring.weights.idleTime')}
            value={config.weight_idle_time}
            onChange={v => setConfig({ ...config, weight_idle_time: v })}
          />
          <WeightSlider
            label={t('compliance.scoring.weights.fuelEfficiency')}
            value={config.weight_fuel_efficiency}
            onChange={v => setConfig({ ...config, weight_fuel_efficiency: v })}
          />
        </div>
        <div className={`mt-4 p-3 rounded-lg ${totalWeight === 100 ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          <span className="font-medium">{t('compliance.scoring.weights.total', { value: totalWeight })}</span>
          {totalWeight !== 100 && <span className="ml-2">{t('compliance.scoring.weights.mustEqual')}</span>}
        </div>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative w-12 h-6 rounded-full transition-colors ${
        checked ? 'bg-green-600' : 'bg-gray-300'
      }`}
    >
      <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
        checked ? 'left-7' : 'left-1'
      }`} />
    </button>
  );
}

function CheckOption({ t, label, description, checked, onChange, recommended, warning }) {
  return (
    <label className="flex items-start cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className="mt-1 mr-3"
      />
      <div>
        <span className="font-medium">{label}</span>
        {recommended && (
          <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded">
            {t('compliance.scoring.recommended')}
          </span>
        )}
        {warning && (
          <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded">
            {warning}
          </span>
        )}
        {description && <p className="text-sm text-gray-500">{description}</p>}
      </div>
    </label>
  );
}

function WeightSlider({ label, value, onChange }) {
  return (
    <div className="flex items-center space-x-4">
      <span className="w-40 text-sm">{label}</span>
      <input
        type="range"
        min="0"
        max="100"
        value={value}
        onChange={e => onChange(parseInt(e.target.value))}
        className="flex-1"
      />
      <span className="w-12 text-right font-mono">{value}%</span>
    </div>
  );
}

export default PerformanceScoringConfig;
